import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useBrandSelection } from './useBrandSelection';
import { useApiTokens } from './useApiTokens';
import { useQuotaManagement } from './useQuotaManagement';
import { ApiCallsAuditService } from '@/services/apiCallsAuditService';
import { toastEdgeFunctionError } from '@/utils/toastEdgeFunction';
import { invokeWithTimeout } from '@/utils/edgeFunctionWithTimeout';

/**
 * Normalizes a filename to be storage-friendly and URL-safe
 * - Converts spaces to hyphens
 * - Removes special characters except hyphens, underscores, and dots
 * - Preserves file extension
 * - Converts to lowercase
 */
const normalizeFilename = (filename: string): string => {
  // Extract file extension
  const lastDotIndex = filename.lastIndexOf('.');
  const name = lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
  const extension = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';
  
  // Normalize the name part:
  // 1. Convert to lowercase
  // 2. Replace spaces with hyphens
  // 3. Remove or replace special characters (keep only alphanumeric, hyphens, underscores)
  // 4. Remove consecutive hyphens
  // 5. Trim leading/trailing hyphens
  const normalizedName = name
    .toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with hyphens
    .replace(/[^a-z0-9\-_]/g, '-')  // Replace special chars with hyphens
    .replace(/-+/g, '-')             // Replace consecutive hyphens with single hyphen
    .replace(/^-+|-+$/g, '');        // Remove leading/trailing hyphens
  
  // Normalize extension (lowercase, remove spaces)
  const normalizedExtension = extension
    .toLowerCase()
    .replace(/\s+/g, '');
  
  return normalizedName + normalizedExtension;
};

export interface GenerationRequest {
  prompt: string;
  inputImage?: File;
  inputImages?: File[]; // Support for multiple input images
  firstFrameFiles?: File[]; // For morphing start frame
  lastFrameFiles?: File[];  // For morphing end frame
  aiProvider: 'openai' | 'gemini' | 'fal_ai' | 'hailuoai' | 'lumalabs' | 'byteplus' | 'google';
  style?: string;
  quality?: string;
  size?: string;
  promptId?: string;
  modelId?: string; // For specific model selection like kling-video
  generationType?: 'image' | 'video'; // Add generation type support
  // Multi-image generation options
  generation_mode?: "single" | "blend" | "batch" | "sequential";
  sequential_image_generation?: "auto" | "disabled";
  sequential_image_generation_options?: { max_images?: number };
}

export interface ArtGeneration {
  id: string;
  brand_id: string;
  user_id: string;
  prompt_id?: string;
  custom_prompt?: string;
  input_image_url?: string;
  generated_image_url: string;
  generated_video_url?: string; // Add support for video output
  ai_provider: string;
  model_used?: string;
  generation_settings: Record<string, any>;
  generation_time_ms?: number;
  cost_estimate?: number;
  created_at: string;
}

export const useVisualGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const { checkQuotaBeforeGeneration, refreshQuotaStatus } = useQuotaManagement();
  const { currentBrandId } = useBrandSelection();
  const { apiTokens } = useApiTokens(currentBrandId);

  // Helpers for BytePlus input normalization
  const parseSize = (size?: string): { width: number; height: number } | null => {
    if (!size) return null;
    const m = size.match(/^(\d+)x(\d+)$/);
    if (!m) return null;
    return { width: parseInt(m[1], 10), height: parseInt(m[2], 10) };
  };

  const fileToImage = (file: File): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
  };

  const normalizeForBytePlus = async (file: File, size?: string): Promise<File> => {
    try {
      const img = await fileToImage(file);
      const ratio = img.naturalWidth / img.naturalHeight;
      if (ratio >= 0.33 && ratio <= 3.0) return file; // already valid

      const target = parseSize(size) || { width: 1024, height: 1024 };
      const canvas = document.createElement('canvas');
      canvas.width = target.width;
      canvas.height = target.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return file;

      // Fill with transparent background
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Fit image inside canvas (contain) and center it
      const scale = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
      const drawW = Math.round(img.naturalWidth * scale);
      const drawH = Math.round(img.naturalHeight * scale);
      const dx = Math.round((canvas.width - drawW) / 2);
      const dy = Math.round((canvas.height - drawH) / 2);
      ctx.drawImage(img, dx, dy, drawW, drawH);

      const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) return file;
      const baseName = normalizeFilename(file.name.replace(/\.(png|jpg|jpeg|webp)$/i, ''));
      const normalized = new File([blob], `${baseName}-normalized.jpg`, { type: 'image/jpeg' });
      return normalized;
    } catch (e) {
      console.warn('BytePlus normalization failed, using original file', e);
      return file;
    }
  };

  // Polling function for VEO3 operations
  const pollVEO3Operation = async (operationName: string, brandId: string): Promise<ArtGeneration | null> => {
    console.log('Starting VEO3 polling for operation:', operationName);
    const maxPollingTime = 300000; // 5 minutes
    const pollInterval = 10000; // 10 seconds
    const startTime = Date.now();
    
    while (Date.now() - startTime < maxPollingTime) {
      setProgress(prev => Math.min(prev + 1, 95)); // Gradually increase progress
      
      try {
        const { data, error } = await supabase.functions.invoke('poll-veo3-operation', {
          body: { operationName, brandId }
        });
        
        if (error) throw error;
        
        if (data?.status === 'completed' && data?.generated_video_url) {
          console.log('VEO3 operation completed successfully');
          return await saveGenerationResult(data, brandId);
        }
        
        if (data?.status === 'failed') {
          throw new Error(data?.error || 'VEO3 operation failed');
        }
        
        // Still processing, wait and try again
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
      } catch (error) {
        console.error('Error polling VEO3 operation:', error);
        throw error;
      }
    }
    
    throw new Error('VEO3 operation timed out after 5 minutes');
  };

  // Helper to save generation result
  const saveGenerationResult = async (data: any, brandId: string): Promise<ArtGeneration | null> => {
    // Validate prompt_id when saving video generations
    let resolvedPromptId = data.promptId || null;
    if (resolvedPromptId) {
      const { data: promptRow } = await supabase
        .from('os_art_prompts')
        .select('id')
        .eq('id', resolvedPromptId)
        .maybeSingle();
      if (!promptRow) {
        resolvedPromptId = null;
      }
    }

    const generationData = {
      brand_id: brandId,
      user_id: (await supabase.auth.getUser()).data.user?.id || '',
      prompt_id: resolvedPromptId,
      custom_prompt: resolvedPromptId ? null : (data.prompt || null),
      input_image_url: null,
      input_image_urls: [],
      generated_image_url: data.generated_video_url, // Use video URL as primary URL
      generated_video_url: data.generated_video_url,
      ai_provider: data.ai_provider || 'google',
      model_used: data.model_used,
      generation_settings: {
        generationType: 'video'
      },
      generation_time_ms: data.processing_time_ms || null,
      cost_estimate: data.cost_estimate || null
    };

    const { data: savedGeneration, error: saveError } = await supabase
      .from('os_art_generations')
      .insert(generationData)
      .select()
      .single();

    if (saveError) throw saveError;
    return savedGeneration as ArtGeneration;
  };

  const generateVisual = async (request: GenerationRequest): Promise<ArtGeneration | null> => {
    console.log('🎬 === VIDEO GENERATION START ===');
    console.log('🎬 Request:', { 
      aiProvider: request.aiProvider, 
      modelId: request.modelId, 
      generationType: request.generationType,
      prompt: request.prompt?.substring(0, 50) + '...'
    });
    
    if (!currentBrandId) {
      console.log('❌ No brand ID found');
      toast.error('No brand selected');
      return null;
    }

    // Check quota before generation
    const canGenerate = await checkQuotaBeforeGeneration();
    if (!canGenerate) {
      return null;
    }

    if (request.aiProvider === 'openai' && !apiTokens.openai) {
      toast.error('OpenAI API key not configured');
      return null;
    }

    if (request.aiProvider === 'gemini' && !apiTokens.gemini) {
      toast.error('Gemini API key not configured');
      return null;
    }

    if (request.aiProvider === 'fal_ai' && !apiTokens.fal_ai) {
      toast.error('Fal AI API key not configured');
      return null;
    }

    if (request.aiProvider === 'hailuoai' && !apiTokens.hailuoai) {
      toast.error('HailuoAI API key not configured');
      return null;
    }

    if (request.aiProvider === 'lumalabs' && !apiTokens.lumalabs) {
      toast.error('LumaLabs API key not configured');
      return null;
    }

    if (request.aiProvider === 'byteplus' && !apiTokens.byteplus) {
      toast.error('BytePlus API key not configured');
      return null;
    }
    // Validate byteplus SeedReal 4.0 input formats at generation time
    const isSeedReal = request.aiProvider === 'byteplus' && (
      request.modelId?.includes('seedreal-4.0') ||
      request.modelId?.includes('seed-real-4.0') ||
      request.modelId?.includes('seedream-4-0-250828')
    );

    if (isSeedReal) {
      const files: File[] = [];
      if (request.inputImages?.length) files.push(...request.inputImages);
      if (request.inputImage) files.push(request.inputImage);

      // Deduplicate files by name to avoid duplicate error messages
      const uniqueFiles = Array.from(new Map(files.map(f => [f.name, f])).values());

      // If there are image inputs, enforce PNG/JPEG only
      if (uniqueFiles.length > 0) {
        const unsupported = uniqueFiles.filter(f => {
          const type = (f.type || '').toLowerCase();
          const name = (f.name || '').toLowerCase();
          const isPNG = type.includes('png') || name.endsWith('.png');
          const isJPEG = type.includes('jpeg') || type.includes('jpg') || name.endsWith('.jpg') || name.endsWith('.jpeg');
          return !(isPNG || isJPEG);
        });
        if (unsupported.length > 0) {
          toast.error(`SeedReal 4.0 doesn't support WebP or other formats. Please use PNG or JPEG images only. Unsupported: ${unsupported.map(f => f.name).join(', ')}`);
          throw new Error(`SeedReal 4.0 supports PNG/JPEG only. Unsupported: ${unsupported.map(f => f.name).join(', ')}`);
        }
      }
    }

    setIsGenerating(true);
    setProgress(0);

    // Create background task that persists across tab switches
    const backgroundGeneration = async (): Promise<ArtGeneration | null> => {
      try {
        // Upload input images if provided
        let inputImageUrl: string | undefined;
        let inputImageUrls: string[] = [];
        
        // Upload first frame images if provided (morphing)
        let firstFrameImageUrls: string[] = [];
        if (request.firstFrameFiles && request.firstFrameFiles.length > 0) {
          setProgress(10);
          const userId = (await supabase.auth.getUser()).data.user?.id;
          if (!userId) throw new Error('User not authenticated');
          
          for (const file of request.firstFrameFiles) {
            const fileName = `${Date.now()}-${normalizeFilename(file.name)}`;
            const filePath = `${userId}/${fileName}`;
            
            const { error: uploadError } = await supabase.storage
              .from('visual-generations-first-frame')
              .upload(filePath, file);

            if (uploadError) throw new Error(`Failed to upload first frame: ${uploadError.message}`);
            
            const { data: { publicUrl } } = supabase.storage
              .from('visual-generations-first-frame')
              .getPublicUrl(filePath);
            
            firstFrameImageUrls.push(publicUrl);
          }
        }

        // Upload last frame images if provided (morphing)
        let lastFrameImageUrls: string[] = [];
        if (request.lastFrameFiles && request.lastFrameFiles.length > 0) {
          setProgress(15);
          const userId = (await supabase.auth.getUser()).data.user?.id;
          if (!userId) throw new Error('User not authenticated');
          
          for (const file of request.lastFrameFiles) {
            const fileName = `${Date.now()}-${normalizeFilename(file.name)}`;
            const filePath = `${userId}/${fileName}`;
            
            const { error: uploadError } = await supabase.storage
              .from('visual-generations-last-frame')
              .upload(filePath, file);

            if (uploadError) throw new Error(`Failed to upload last frame: ${uploadError.message}`);
            
            const { data: { publicUrl } } = supabase.storage
              .from('visual-generations-last-frame')
              .getPublicUrl(filePath);
            
            lastFrameImageUrls.push(publicUrl);
          }
        }
        
        // Handle multiple images
        if (request.inputImages && request.inputImages.length > 0) {
          setProgress(20);
          
          for (let i = 0; i < request.inputImages.length; i++) {
            const originalFile = request.inputImages[i];
            const file = request.aiProvider === 'byteplus'
              ? await normalizeForBytePlus(originalFile, request.size)
              : originalFile;
            const fileName = `${Date.now()}-${normalizeFilename(file.name)}`;
            const filePath = `${currentBrandId}/inputs/${fileName}`;
            
            const { error: uploadError } = await supabase.storage
              .from('visual-generations')
              .upload(filePath, file);

            if (uploadError) throw uploadError;
            
            const { data: { publicUrl } } = supabase.storage
              .from('visual-generations')
              .getPublicUrl(filePath);
            
            inputImageUrls.push(publicUrl);
            
            // Set the first image as the main input for backward compatibility
            if (i === 0) {
              inputImageUrl = publicUrl;
            }
          }
          setProgress(40);
        }
        // Handle single image for backward compatibility
        else if (request.inputImage) {
          setProgress(20);
          const originalFile = request.inputImage;
          const file = request.aiProvider === 'byteplus'
            ? await normalizeForBytePlus(originalFile, request.size)
            : originalFile;
          const fileName = `${Date.now()}-${normalizeFilename(file.name)}`;
          const filePath = `${currentBrandId}/inputs/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('visual-generations')
            .upload(filePath, file);

          if (uploadError) throw uploadError;
          
          const { data: { publicUrl } } = supabase.storage
            .from('visual-generations')
            .getPublicUrl(filePath);
          
          inputImageUrl = publicUrl;
          inputImageUrls = [publicUrl];
          setProgress(40);
        }

        // Call the visual generation edge function - this continues in background
        
        // Final safeguard: if SeedReal 4.0 is selected, ensure uploaded URLs are PNG/JPEG
        if (isSeedReal && inputImageUrls && inputImageUrls.length > 0) {
          const unsupportedUrls = inputImageUrls.filter(u => {
            const lower = u.toLowerCase().split('?')[0];
            return lower.endsWith('.webp') || lower.endsWith('.gif') || lower.endsWith('.svg') ||
                   lower.endsWith('.heic') || lower.endsWith('.heif') || lower.endsWith('.bmp') ||
                   lower.endsWith('.tiff') || lower.endsWith('.tif') || lower.endsWith('.avif');
          });
          if (unsupportedUrls.length > 0) {
            console.error('❌ SeedReal 4.0 blocked due to unsupported formats:', unsupportedUrls);
            toast.error("SeedReal 4.0 doesn't support WebP or other formats. Please use PNG or JPEG images only.");
            throw new Error(`SeedReal 4.0 supports PNG/JPEG only. Unsupported: ${unsupportedUrls.map(u => u.split('/').pop()).join(', ')}`);
          }
        }

        setProgress(60);
        
        console.log('=== Calling edge function (background task) ===');
        console.log('Request data:', { 
          aiProvider: request.aiProvider, 
          modelId: request.modelId, 
          generationType: request.generationType,
          selectedFiles: request.inputImages?.length || 0,
          inputImageUrls: inputImageUrls.length,
          generation_mode: request.generation_mode
        });
        
        // Call generate-visual edge function
        const { data, error } = await invokeWithTimeout(supabase, 'generate-visual', {
          timeout: 300000, // 5 minute timeout for visual generation
          body: {
            prompt: request.prompt,
            inputImageUrl,
            inputImageUrls,
            firstFrameImageUrls,
            lastFrameImageUrls,
            aiProvider: request.aiProvider,
            style: request.style,
            quality: request.quality,
            size: request.size,
            brandId: currentBrandId,
            promptId: request.promptId,
            modelId: request.modelId,
            generationType: request.generationType,
            generation_mode: request.generation_mode || "single",
            sequential_image_generation: request.sequential_image_generation || "disabled",
            sequential_image_generation_options: request.sequential_image_generation_options || {}
          }
        });

        if (error) {
          console.error('Edge function error:', error);
          throw error;
        }
        
        console.log('=== Edge function response ===');
        console.log('Response data:', data);
        
        // Handle async video generation (HailuoAI returns task_id and requires polling)
        if (data?.status === 'processing' && data?.task_id && request.aiProvider === 'hailuoai') {
          console.log('HailuoAI video generation started, polling for completion...');
          const taskId = data.task_id;
          const maxWaitMs = 600000; // 10 minutes max wait
          const pollInterval = 10000; // Poll every 10 seconds
          const pollStart = Date.now();
          
          while (Date.now() - pollStart < maxWaitMs) {
            setProgress(prev => Math.min(prev + 2, 95));
            await new Promise(resolve => setTimeout(resolve, pollInterval));
            
            // Poll for status
            const { data: statusData, error: statusError } = await invokeWithTimeout(supabase, 'generate-visual', {
              timeout: 30000,
              body: {
                action: 'status',
                task_id: taskId,
                aiProvider: request.aiProvider,
                brandId: currentBrandId
              }
            });
            
            if (statusError) {
              console.error('Status poll error:', statusError);
              // Don't immediately fail - continue polling unless it's a critical error
              if (statusError.message?.includes('Rate limit') || statusError.message?.includes('Payment required')) {
                throw statusError; // Critical errors should stop polling
              }
              continue;
            }
            
            console.log('Poll status:', statusData?.status);
            
            if (statusData?.status === 'completed') {
              console.log('✅ Video generation completed successfully!');
              // Use the completed video data
              data.generated_video_url = statusData.generated_video_url;
              data.ai_provider = statusData.ai_provider;
              data.model_used = statusData.model_used;
              data.status = 'completed'; // Explicitly mark as completed
              break;
            } else if (statusData?.status === 'failed') {
              throw new Error(statusData.error || 'Video generation failed');
            }
            // Continue polling if still processing
          }
          
          if (!data.generated_video_url) {
            throw new Error('Video generation timed out after 10 minutes');
          }
        }
        
        // Handle VEO3 processing status
        if (data?.status === 'processing' && data?.operation_name) {
          console.log('VEO3 operation is processing, starting client-side polling...');
          return await pollVEO3Operation(data.operation_name, currentBrandId);
        }
        
        // Check for both possible response formats (image or video)
        if (!data?.generatedImageUrl && !data?.generated_image_url && !data?.generated_video_url) {
          console.error('No generated content URL in response:', data);
          throw new Error('No image or video generated');
        }
        
        // Handle both response formats for different AI providers
        // For video-only generations, use video URL as the primary URL
        const generatedImageUrl = data.generatedImageUrl || data.generated_image_url || data.generated_video_url;

        setProgress(90);

        // Validate prompt_id against os_art_prompts; fallback to custom_prompt when not found
        let resolvedPromptId = request.promptId || null;
        if (resolvedPromptId) {
          const { data: promptRow } = await supabase
            .from('os_art_prompts')
            .select('id')
            .eq('id', resolvedPromptId)
            .maybeSingle();
          if (!promptRow) {
            resolvedPromptId = null;
          }
        }

        // Prepare response-derived multi-image support
        const responseImageUrls: string[] = Array.isArray(data.generated_image_urls)
          ? data.generated_image_urls
          : (generatedImageUrl ? [generatedImageUrl] : []);
        const responseMultiType: 'single' | 'batch' | 'blend' | 'sequential' = (data.generation_type as any) || 'single';

        // Common base for DB rows
        const commonData = {
          brand_id: currentBrandId,
          user_id: (await supabase.auth.getUser()).data.user?.id || '',
          prompt_id: resolvedPromptId,
          custom_prompt: resolvedPromptId ? null : request.prompt,
          input_image_url: inputImageUrl || null,
          input_image_urls: inputImageUrls || [],
          first_frame_image_urls: firstFrameImageUrls || [], // Add first frame URLs
          last_frame_image_urls: lastFrameImageUrls || [],   // Add last frame URLs
          generated_video_url: data.generated_video_url || null,
          ai_provider: request.aiProvider,
          model_used: data.model_used || null,
          generation_settings: {
            style: request.style,
            quality: request.quality,
            size: request.size,
            generationType: request.generationType, // image | video
            multi: {
              type: responseMultiType,
              imageUrls: responseImageUrls,
            },
          },
          generation_time_ms: data.processing_time_ms || null,
          cost_estimate: data.cost_estimate || null,
        } as const;

        let savedGeneration: ArtGeneration | null = null;

        if (responseImageUrls.length > 1) {
          // Insert one row per variant so the gallery can show all
          const rows = responseImageUrls.map((url, idx) => ({
            ...commonData,
            generated_image_url: url,
            generation_settings: {
              ...commonData.generation_settings,
              multi: {
                ...commonData.generation_settings.multi,
                index: idx + 1,
                total: responseImageUrls.length,
              },
            },
          }));

          const { data: inserted, error: insertErr } = await supabase
            .from('os_art_generations')
            .insert(rows)
            .select();

          if (insertErr) throw insertErr;
          savedGeneration = (inserted && inserted[0]) as ArtGeneration;
        } else {
          // Single image/video
          const singleRow = {
            ...commonData,
            generated_image_url: generatedImageUrl,
          };

          const { data: inserted, error: insertErr } = await supabase
            .from('os_art_generations')
            .insert(singleRow)
            .select()
            .single();
          if (insertErr) throw insertErr;
          savedGeneration = inserted as ArtGeneration;
        }

        setProgress(100);
        
        // Log the API call for quota tracking (background task)
        console.log('=== About to log API call (background) ===');
        console.log('Audit data being sent:', {
          source_menu_item: 'visual_generation',
          platform: request.aiProvider === 'fal_ai' ? 'fal_ai' : (request.aiProvider === 'openai' ? 'openai' : 'gemini'),
          generation_type: request.generationType || 'image',
          model_used: data.model_used || request.modelId || null,
          cost_estimate: data.costEstimate || 0,
        });
        
        await ApiCallsAuditService.logApiCall({
          source_menu_item: 'visual_generation',
          platform: request.aiProvider === 'fal_ai' ? 'fal_ai' : (request.aiProvider === 'openai' ? 'openai' : 'gemini'),
          endpoint: '/generate-visual',
          method: 'POST',
          status_code: 200,
          cost_estimate: data.cost_estimate || 0,
          brand_id: currentBrandId,
          generation_type: request.generationType || 'image',
          model_used: data.model_used || request.modelId || null,
        });
        
        console.log('=== API call logged successfully (background) ===');
        
        // Refresh quota status after generation
        await refreshQuotaStatus();
        
        // Show success notification (will only show if user is still on the page)
        const count = responseImageUrls.length || 1;
        toast.success(count > 1 ? `Generated ${count} variants` : 'Visual generated successfully!');
        
        return savedGeneration as ArtGeneration;
      } catch (error) {
        console.error('Error generating visual (background):', error);
        
        // Log failed API call for quota tracking
        try {
          await ApiCallsAuditService.logApiCall({
            source_menu_item: 'visual_generation',
            platform: request.aiProvider === 'fal_ai' ? 'fal_ai' : (request.aiProvider === 'openai' ? 'openai' : 'gemini'),
            endpoint: '/generate-visual',
            method: 'POST',
            status_code: 500,
            cost_estimate: 0,
            brand_id: currentBrandId,
            generation_type: request.generationType || 'image',
            model_used: request.modelId || null,
          });
        } catch (auditError) {
          console.error('Failed to log failed API call:', auditError);
        }
        
        // Show error notification (will only show if user is still on the page)
        toastEdgeFunctionError('generate-visual', error, 'Failed to generate visual');
        return null;
      }
    };

    try {
      // Start the background generation
      console.log('=== Starting background generation task ===');
      const result = await backgroundGeneration();
      return result;
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  return {
    generateVisual,
    isGenerating,
    progress
  };
};